import React, { useState } from 'react';
import { saveAs } from 'file-saver';
import { getOperationalReport } from '../../shared/api/tools';
import { FMEPageHeader } from '../Layout';
import { TailwindButton } from '../Tailwind';
import { cn } from '../../lib/utils';
import OperationalScenarioSelector from './OperationalScenarioSelector';

const getYearsBetween = (startDate: string, endDate: string): number[] => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const years: number[] = [];
    for (let year = start.getFullYear(); year <= end.getFullYear(); year++) {
        years.push(year);
    }
    return years;
};

interface OperationalToolsPageProps {
    model: any;
    currentScenario: any;
    currentSensitivity: any;
    operationalParameters: any;
}

export default function OperationalToolsPage({
    model,
    currentScenario,
    currentSensitivity,
    operationalParameters,
}: OperationalToolsPageProps) {
    const [loading, setLoading] = useState<string | null>(null);
    const [frequency, setFrequency] = useState('');
    const [monthlySelection, setMonthlySelection] = useState<Record<number, string>>({});
    const [quarterlySelection, setQuarterlySelection] = useState<Record<number, string>>({});
    const [annualSelection, setAnnualSelection] = useState<Record<number, string>>({});

    const years = operationalParameters?.global_timing
        ? getYearsBetween(operationalParameters.global_timing.start_date, operationalParameters.global_timing.end_date)
        : [];

    const files = [{ name: 'Operational Report', routeName: 'operational_report', description: 'Download the operational report for your model' }];

    const getSelection = (index: number) => {
        if (frequency === 'Monthly') return `m${annualSelection[index]}${monthlySelection[index]}`;
        if (frequency === 'Quarterly') return `q${annualSelection[index]}${quarterlySelection[index]}`;
        if (frequency === 'Annually') return `a${annualSelection[index]}`;
        return '';
    };

    const isDownloadEnabled = (index: number) => {
        if (frequency === 'Monthly') return Boolean(monthlySelection[index] && annualSelection[index]);
        if (frequency === 'Quarterly') return Boolean(quarterlySelection[index] && annualSelection[index]);
        if (frequency === 'Annually') return Boolean(annualSelection[index]);
        return false;
    };

    const handleDownload = async (fileName: string, routeName: string, period: string) => {
        setLoading(routeName);
        try {
            const response = await getOperationalReport(currentScenario.id, routeName, period);
            if (response) {
                const blob = await response.blob();
                saveAs(blob, `${model.name} - ${currentScenario.name} - ${fileName}`);
            }
        } catch (error) {
            console.error('Error downloading report:', error);
        } finally {
            setLoading(null);
        }
    };

    const frequencyOptions = [
        { value: 'Monthly', label: 'Monthly' },
        { value: 'Quarterly', label: 'Quarterly' },
        { value: 'Annually', label: 'Annually' },
    ];

    const monthOptions = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(
        (month) => ({ value: month, label: month })
    );

    const quarterOptions = ['Q1', 'Q2', 'Q3', 'Q4'].map((quarter) => ({ value: quarter, label: quarter }));

    const yearOptions = years.map((year) => ({ value: year.toString(), label: year.toString() }));

    return (
        <div className="flex flex-col h-full">
            <FMEPageHeader
                title="Tools & Reports"
                breadcrumb={[model.name, currentScenario.name, ...(currentSensitivity ? [currentSensitivity.name] : [])]}
                description="Download reports and access tools for your operational model"
                actions={<OperationalScenarioSelector />}
            />

            <div className="px-8 py-6 flex-1 overflow-y-auto">
                <div className="flex flex-col gap-6">
                    {/* Reports Section */}
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <h2 className="text-xl font-semibold text-slate-900">Reports</h2>
                            <p className="text-slate-600">
                                Download operational reports for your financial model. Select a frequency and period before downloading.
                            </p>
                        </div>

                        <div className="flex flex-col gap-3">
                            {files.map((file, index) => (
                                <div
                                    key={index}
                                    className="flex items-center gap-4 p-5 border border-slate-200 rounded-xl bg-white shadow-sm hover:border-primary/40 hover:shadow-md transition-all"
                                >
                                    <div className="flex items-center justify-center w-11 h-11 shrink-0 rounded-xl bg-primary/10 text-primary">
                                        <span className="material-symbols-outlined text-[22px]">description</span>
                                    </div>
                                    <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                        <span className="text-slate-900 font-semibold text-base">
                                            {file.name}
                                        </span>
                                        <span className="text-sm text-slate-600">
                                            {file.description}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <select
                                            value={frequency}
                                            onChange={(e) => setFrequency(e.target.value)}
                                            className={cn(
                                                'bg-transparent border border-slate-200 rounded-md text-slate-900 text-sm pl-3 pr-7 py-1.5 font-medium cursor-pointer',
                                                'focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/30'
                                            )}
                                        >
                                            <option value="" disabled>Frequency</option>
                                            {frequencyOptions.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>

                                        {frequency === 'Monthly' && (
                                            <select
                                                value={monthlySelection[index] || ''}
                                                onChange={(e) => setMonthlySelection((prev) => ({ ...prev, [index]: e.target.value }))}
                                                className={cn(
                                                    'bg-transparent border border-slate-200 rounded-md text-slate-900 text-sm pl-3 pr-7 py-1.5 font-medium cursor-pointer',
                                                    'focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/30'
                                                )}
                                            >
                                                <option value="" disabled>Month</option>
                                                {monthOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        )}

                                        {frequency === 'Quarterly' && (
                                            <select
                                                value={quarterlySelection[index] || ''}
                                                onChange={(e) => setQuarterlySelection((prev) => ({ ...prev, [index]: e.target.value }))}
                                                className={cn(
                                                    'bg-transparent border border-slate-200 rounded-md text-slate-900 text-sm pl-3 pr-7 py-1.5 font-medium cursor-pointer',
                                                    'focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/30'
                                                )}
                                            >
                                                <option value="" disabled>Quarter</option>
                                                {quarterOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        )}

                                        {frequency && (
                                            <select
                                                value={annualSelection[index] || ''}
                                                onChange={(e) => setAnnualSelection((prev) => ({ ...prev, [index]: e.target.value }))}
                                                className={cn(
                                                    'bg-transparent border border-slate-200 rounded-md text-slate-900 text-sm pl-3 pr-7 py-1.5 font-medium cursor-pointer',
                                                    'focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/30'
                                                )}
                                            >
                                                <option value="" disabled>Year</option>
                                                {yearOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        )}

                                        <TailwindButton
                                            variant="secondary"
                                            size="sm"
                                            icon={loading === file.routeName ? undefined : 'download'}
                                            onClick={() => handleDownload(file.name, file.routeName, getSelection(index))}
                                            disabled={loading !== null || !isDownloadEnabled(index)}
                                        >
                                            {loading === file.routeName ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900"></div>
                                                    Downloading...
                                                </div>
                                            ) : (
                                                'Download'
                                            )}
                                        </TailwindButton>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Custom Documents Section */}
                    <div className="flex flex-col gap-4 mt-4">
                        <h2 className="text-xl font-semibold text-slate-900">Custom Documents</h2>
                        <div className="flex items-center gap-4 p-5 border-2 border-slate-200 rounded-lg bg-slate-50/50">
                            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg shrink-0">
                                <span className="material-symbols-outlined text-primary text-[22px]">contact_support</span>
                            </div>
                            <div className="flex flex-col gap-1">
                                <p className="text-sm text-slate-600">
                                    All documents can be customized upon request. Please contact the Peerception team to discuss your specific needs.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
